// Minimal Fernet token decryption using Web Crypto API
// Spec: https://github.com/fernet/spec

// Utility: Base64 URL-safe to standard Base64
function base64UrlToBase64(b64url) {
  const cleaned = (b64url || '').replace(/\s+/g, '').trim().replace(/-/g, '+').replace(/_/g, '/');
  const rem = cleaned.length % 4;
  return rem === 0 ? cleaned : cleaned + '='.repeat(4 - rem);
}

function base64ToUint8Array(b64) {
  const binary = typeof atob !== 'undefined' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function uint8ArrayToString(bytes) {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(bytes);
  }
  // Fallback
  return Buffer.from(bytes).toString('utf-8');
}

function concatUint8Arrays(...arrays) {
  const total = arrays.reduce((acc, a) => acc + a.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

function constantTimeEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

function pkcs7Unpad(buf) {
  if (buf.length === 0) throw new Error('Invalid padding: empty buffer');
  const pad = buf[buf.length - 1];
  if (pad < 1 || pad > 16) throw new Error('Invalid padding');
  for (let i = buf.length - pad; i < buf.length; i++) {
    if (buf[i] !== pad) throw new Error('Invalid padding');
  }
  return buf.subarray(0, buf.length - pad);
}

function forgivingUnpad(buf) {
  // Try strict PKCS7 first
  try {
    const out = pkcs7Unpad(buf);
    return { bytes: out, method: 'pkcs7' };
  } catch (_) {
    // Try zero padding (strip trailing 0x00)
    let end = buf.length;
    while (end > 0 && buf[end - 1] === 0) end--;
    if (end > 0 && end !== buf.length) {
      return { bytes: buf.subarray(0, end), method: 'zero' };
    }
    return { bytes: buf, method: 'none' };
  }
}

async function importHmacKey(signKey) {
  return crypto.subtle.importKey(
    'raw',
    signKey,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify']
  );
}

async function importAesKey(encKey) {
  return crypto.subtle.importKey(
    'raw',
    encKey,
    { name: 'AES-CBC' },
    false,
    ['decrypt']
  );
}

export async function fernetDecrypt(tokenB64Url, keyB64Url) {
  // Accept key in multiple encodings: base64url(32 bytes) or base64-of-base64url
  const keyStr = (keyB64Url || '').trim();
  const keyCandidates = [];

  // Candidate A: interpret as base64url for 32 raw bytes
  try {
    const k = base64ToUint8Array(base64UrlToBase64(keyStr));
    if (k.length === 32) keyCandidates.push({ bytes: k, label: 'direct-b64url' });
  } catch (_) { /* ignore */ }

  // Candidate B: interpret as standard base64 of an inner base64url string
  try {
    const innerAsBytes = base64ToUint8Array(keyStr); // standard base64 decode
    const innerStr = uint8ArrayToString(innerAsBytes).trim();
    const k2 = base64ToUint8Array(base64UrlToBase64(innerStr));
    if (k2.length === 32) keyCandidates.push({ bytes: k2, label: 'double-base64' });
  } catch (_) { /* ignore */ }

  if (keyCandidates.length === 0) throw new Error('Fernet key must decode to 32 bytes');

  // Decode token
  const tokenStr = (tokenB64Url || '').trim();
  const tokenBytes = base64ToUint8Array(base64UrlToBase64(tokenStr));
  if (tokenBytes.length < 1 + 8 + 16 + 32) throw new Error('Invalid token length');

  const version = tokenBytes[0];
  if (version !== 0x80) throw new Error('Unsupported Fernet version');
  const ts = tokenBytes.subarray(1, 9); // timestamp (unused)
  const iv = tokenBytes.subarray(9, 25); // 16 bytes
  const hmac = tokenBytes.subarray(tokenBytes.length - 32); // last 32 bytes
  const ciphertext = tokenBytes.subarray(25, tokenBytes.length - 32);

  const macInput = concatUint8Arrays(new Uint8Array([version]), ts, iv, ciphertext);

  let lastError = null;
  for (const keyCand of keyCandidates) {
    const keyBytes = keyCand.bytes;
    const layouts = [
      { signKey: keyBytes.subarray(0, 16), encKey: keyBytes.subarray(16, 32), label: `spec (${keyCand.label})` },
      { signKey: keyBytes.subarray(16, 32), encKey: keyBytes.subarray(0, 16), label: `reversed (${keyCand.label})` },
    ];
    for (const layout of layouts) {
    try {
      // Verify HMAC with this layout
      const hmacKey = await importHmacKey(layout.signKey);
      const mac = new Uint8Array(await crypto.subtle.sign('HMAC', hmacKey, macInput));
      if (!constantTimeEqual(mac, hmac)) {
        lastError = new Error('Invalid token HMAC');
        continue; // try next layout
      }

      // HMAC matched for this layout
      console.debug('[Fernet] HMAC matched using layout:', layout.label);

      // Try a set of candidate AES keys (handle non-standard implementations)
      const concat = (a, b) => {
        const out = new Uint8Array(a.length + b.length);
        out.set(a, 0); out.set(b, a.length);
        return out;
      };
      const sha256 = async (bytes) => {
        const digest = await crypto.subtle.digest('SHA-256', bytes);
        return new Uint8Array(digest);
      };

      const candidates = [];
      // AES-128 typical
      candidates.push({ keyBytes: layout.encKey, label: 'encKey(128)' });
      candidates.push({ keyBytes: layout.signKey, label: 'signKey(128)' });
      // AES-256 variants sometimes used by custom implementations
      candidates.push({ keyBytes: concat(layout.signKey, layout.encKey), label: 'sign|enc(256)' });
      candidates.push({ keyBytes: concat(layout.encKey, layout.signKey), label: 'enc|sign(256)' });
      // Hash-derived keys
      const encHash = await sha256(layout.encKey);
      const signHash = await sha256(layout.signKey);
      candidates.push({ keyBytes: encHash, label: 'SHA256(encKey)(256)' });
      candidates.push({ keyBytes: signHash, label: 'SHA256(signKey)(256)' });

      for (const cand of candidates) {
        try {
          const aesKey = await importAesKey(cand.keyBytes);
          const plaintextPadded = new Uint8Array(
            await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, aesKey, ciphertext)
          );
          console.debug('[Fernet] AES decrypt using', cand.label, 'produced', plaintextPadded.length, 'bytes');
          const { bytes: plainTry, method } = forgivingUnpad(plaintextPadded);
          const text = uint8ArrayToString(plainTry);
          console.debug('[Fernet] Unpad method', method, 'with', cand.label, '-> bytes:', plainTry.length);
          // Heuristic acceptance: if it looks like our expected pipe-delimited or contains a JWT or JSON
          if (text.includes('|') || text.includes('eyJ')) {
            return text;
          }
          // If strict PKCS7 worked we would have returned above; continue trying other candidates otherwise
        } catch (inner) {
          // continue trying
          // console.debug('[Fernet] candidate', cand.label, 'failed:', inner?.message || inner);
        }
      }

      throw new Error('All AES key candidates failed padding');
    } catch (e) {
      console.debug('[Fernet] Attempt with layout failed:', e?.message || e);
      lastError = e;
      // try next layout
    }
  }
  }

  throw lastError || new Error('Failed to decrypt Fernet token');
}
